import { useEffect, useState } from 'react';
import { ClipboardCheck, Loader2, Play, Check, AlertTriangle } from 'lucide-react';
import * as api from '../../lib/api';
import { useShiftStore } from '../../store/useShiftStore';

const FINDINGS = [
  { value: 'as_expected', label: 'As expected', tone: 'emerald' },
  { value: 'empty', label: 'Empty', tone: 'amber' },
  { value: 'wrong_unit', label: 'Wrong unit', tone: 'amber' },
  { value: 'unauthorized_vehicle', label: 'Unauthorized', tone: 'rose' },
  { value: 'unidentified', label: 'Unidentified', tone: 'amber' },
  { value: 'damaged', label: 'Damaged', tone: 'rose' },
];

const TONE = {
  emerald: 'border-emerald-600 bg-emerald-600 text-white',
  amber: 'border-amber-500 bg-amber-500 text-white',
  rose: 'border-rose-600 bg-rose-600 text-white',
};

export default function AuditPage() {
  const facilityId = useShiftStore((s) => s.facilityId);
  const [audit, setAudit] = useState(null);
  const [worksheet, setWorksheet] = useState([]);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const loadHistory = async () => {
    if (!facilityId) return;
    const { audits } = await api.listAudits(facilityId);
    setHistory(audits);
  };

  useEffect(() => { setAudit(null); setWorksheet([]); loadHistory(); }, [facilityId]);

  const start = async () => {
    setBusy(true);
    setError(null);
    try {
      const { audit: created } = await api.startAudit(facilityId);
      const detail = await api.getAudit(facilityId, created.public_id);
      setAudit(detail.audit);
      setWorksheet(detail.worksheet);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  /**
   * Record optimistically. An attendant walking a lot has patchy signal, and
   * a tap that appears to do nothing gets tapped again — which is exactly the
   * double-entry the upsert on the server protects against, but the UI should
   * not invite it in the first place.
   */
  const record = async (unit, finding) => {
    setWorksheet((rows) => rows.map((r) =>
      r.unit_public_id === unit.unit_public_id ? { ...r, finding, _pending: true } : r));
    try {
      const res = await api.recordAuditItem(facilityId, audit.public_id, {
        unit_id: unit.unit_public_id,
        finding,
        observed_plate: unit.license_plate ?? null,
      });
      setAudit((a) => ({ ...a, ...res.progress }));
      setWorksheet((rows) => rows.map((r) =>
        r.unit_public_id === unit.unit_public_id ? { ...r, finding, _pending: false } : r));
    } catch (err) {
      setError(err.message);
      // Roll the row back so the sheet never shows a finding the server rejected.
      setWorksheet((rows) => rows.map((r) =>
        r.unit_public_id === unit.unit_public_id ? { ...r, finding: null, _pending: false } : r));
    }
  };

  const finish = async () => {
    setBusy(true);
    try {
      await api.completeAudit(facilityId, audit.public_id, null);
      setAudit(null);
      setWorksheet([]);
      await loadHistory();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (!audit) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-6 sm:px-6">
        <h1 className="text-xl font-extrabold tracking-tight text-slate-900">Lot audits</h1>
        <p className="mt-1 text-sm text-slate-600">
          Walk the yard and confirm what is actually in each space.
        </p>

        <button type="button" onClick={start} disabled={busy || !facilityId} className="btn-primary mt-4 w-full py-4 text-base">
          {busy ? <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" /> : <Play className="h-5 w-5" aria-hidden="true" />}
          Start a new audit
        </button>

        {error && <p role="alert" className="mt-3 rounded-lg bg-rose-50 p-3 text-sm text-rose-800">{error}</p>}

        <h2 className="mt-8 text-sm font-bold text-slate-900">Past audits</h2>
        <ul className="mt-3 divide-y divide-slate-200 overflow-hidden rounded-xl border border-slate-200 bg-white">
          {history.length === 0 && <li className="p-4 text-sm text-slate-500">No audits recorded yet.</li>}
          {history.map((a) => (
            <li key={a.public_id} className="flex flex-wrap items-center gap-x-4 gap-y-1 p-4 text-xs">
              <span className="font-semibold text-slate-700">
                {new Date(a.started_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </span>
              <span className="text-slate-600">{a.attendant_first_name} {a.attendant_last_name}</span>
              <span className="text-slate-600">{a.units_checked}/{a.units_total} checked</span>
              {a.exceptions > 0 ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 font-bold text-amber-900">
                  <AlertTriangle className="h-3 w-3" aria-hidden="true" /> {a.exceptions} exception{a.exceptions === 1 ? '' : 's'}
                </span>
              ) : (
                <span className="rounded-full bg-emerald-100 px-2 py-0.5 font-bold text-emerald-800">All clear</span>
              )}
              {!a.completed_at && <span className="text-slate-400">in progress</span>}
              {a.note && <span className="w-full text-slate-500">{a.note}</span>}
            </li>
          ))}
        </ul>
      </div>
    );
  }

  const checked = worksheet.filter((r) => r.finding).length;

  return (
    <div className="mx-auto max-w-3xl px-4 py-6 sm:px-6">
      <div className="sticky top-0 z-10 -mx-4 mb-4 border-b border-slate-200 bg-slate-50/95 px-4 py-3 backdrop-blur sm:-mx-6 sm:px-6">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="flex items-center gap-2 text-lg font-extrabold text-slate-900">
              <ClipboardCheck className="h-5 w-5 text-brand-600" aria-hidden="true" /> Audit in progress
            </h1>
            <p className="text-xs text-slate-600">
              {checked} of {worksheet.length} checked
              {audit.exceptions > 0 && ` · ${audit.exceptions} exception${audit.exceptions === 1 ? '' : 's'}`}
            </p>
          </div>
          <button type="button" onClick={finish} disabled={busy} className="btn-primary shrink-0">
            <Check className="h-4 w-4" aria-hidden="true" /> Finish
          </button>
        </div>
        <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-200">
          <div className="h-full rounded-full bg-brand-600 transition-all"
               style={{ width: `${worksheet.length ? (checked / worksheet.length) * 100 : 0}%` }} />
        </div>
      </div>

      {error && <p role="alert" className="mb-3 rounded-lg bg-rose-50 p-3 text-sm text-rose-800">{error}</p>}

      <ul className="space-y-3">
        {worksheet.map((row) => (
          <li key={row.unit_public_id}
              className={`rounded-xl border bg-white p-4 ${row.finding ? 'border-slate-200 opacity-80' : 'border-slate-300'}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-extrabold text-slate-900">Unit {row.unit_number}</p>
                <p className="mt-0.5 text-xs text-slate-600">
                  {row.expected_lease_id
                    ? <>Expect <strong>{row.vehicle_label ?? 'a vehicle'}</strong>
                        {row.license_plate && <> · <span className="font-mono">{row.license_plate}</span></>}
                        {' '}({row.first_name} {row.last_name})</>
                    : <span className="text-slate-500">Expect empty — {row.unit_status}</span>}
                </p>
              </div>
              {row._pending && <Loader2 className="h-4 w-4 shrink-0 animate-spin text-brand-500" aria-hidden="true" />}
            </div>

            <div className="mt-3 flex flex-wrap gap-1.5">
              {FINDINGS.map((f) => (
                <button
                  key={f.value}
                  type="button"
                  onClick={() => record(row, f.value)}
                  aria-pressed={row.finding === f.value}
                  className={`rounded-lg border px-3 py-2 text-xs font-bold transition ${
                    row.finding === f.value ? TONE[f.tone] : 'border-slate-300 bg-white text-slate-700 hover:border-slate-400'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
